import * as React from "react";
import { Typography, Box, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { BreadcrumbItem } from "components/Breadcrumbs/Breadcrumbs";
import { FileTypesByClientReportsList } from "./FileTypesByClientReportsList";
import { useMutation, useQueryClient } from "react-query";
import { MetricsService } from "gen/clients";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { Form, Formik } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DATE_PICKER_FORMAT, DatePickerField } from "components/formikFields/DatePickerField/DatePickerField";
import AddIcon from "@mui/icons-material/Add";

dayjs.extend(utc);
dayjs.extend(timezone);

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/fileTypesByClientReports",
    label: "File Types Uploaded by Client",
    active: true
  }
];

enum FieldNames {
  from = "from",
  to = "to"
}

interface FormValues {
  [FieldNames.from]: string;
  [FieldNames.to]: string;
}

const FileTypesByClientReportsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [hasRunningReports, setHasRunningReports] = React.useState(false);

  const initialValues: FormValues = React.useMemo(() => {
    const now = new Date();
    const defaultFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      from: dayjs(defaultFrom).format(DATE_PICKER_FORMAT),
      to: dayjs(now).format(DATE_PICKER_FORMAT)
    };
  }, []);

  const {
    mutate: createReport,
    isLoading: isCreating,
    error: createError,
    reset: resetCreateError
  } = useMutation(MetricsService.createFileTypesByClientReport, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listFileTypesByClientReports"]);
      setDialogOpen(false);
    }
  });

  const handleCreateReportClick = React.useCallback(() => {
    resetCreateError();
    setDialogOpen(true);
  }, [resetCreateError]);

  const handleCloseDialog = React.useCallback(() => {
    if (!isCreating) {
      setDialogOpen(false);
    }
  }, [isCreating]);

  const handleRunningReportsChange = React.useCallback((hasRunning: boolean) => {
    setHasRunningReports(hasRunning);
  }, []);

  const validate = React.useCallback((values: FormValues) => {
    const fromValue = dayjs(values[FieldNames.from], DATE_PICKER_FORMAT);
    const toValue = dayjs(values[FieldNames.to], DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};
    if (!values[FieldNames.from]) {
      errors[FieldNames.from] = "Start date is required";
    }
    if (!values[FieldNames.to]) {
      errors[FieldNames.to] = "End date is required";
    }
    if (toValue.isBefore(fromValue)) {
      errors[FieldNames.to] = "Start date must be before end date";
    } else if (toValue.subtract(365, "day").isAfter(fromValue)) {
      errors[FieldNames.to] = "Search range cannot exceed 365 days";
    }
    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toISOString();
      const toValue = dayjs
        .tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .endOf("day")
        .toISOString();
      createReport({
        requestBody: {
          fromDate: fromValue,
          toDate: toValue
        }
      });
    },
    [createReport]
  );

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={<Typography variant="h6">File Types Uploaded by Client</Typography>}
      breadcrumbs={breadcrumbs}
    >
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Generate and view reports detailing the number of files uploaded by each client for Document Translation by
          file type. Reports are generated asynchronously and can be downloaded when complete.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateReportClick}
          disabled={isCreating || hasRunningReports}
          sx={{ ml: 2, whiteSpace: "nowrap" }}
        >
          {isCreating ? "Creating..." : hasRunningReports ? "Report Generation in Progress..." : "Generate New Report"}
        </Button>
      </Box>

      <FileTypesByClientReportsList onRunningReportsChange={handleRunningReportsChange} />
      {createError && <SnackbarApiError error={createError} />}

      {dialogOpen && (
        <Dialog open={true} onClose={handleCloseDialog} fullWidth={true} maxWidth="sm">
          <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit} validateOnMount={false}>
            <Form noValidate={true} autoComplete="off" autoCorrect="off">
              <DialogTitle>Generate File Types By Client Report</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select the date range for the report. The report will be generated asynchronously and may take several
                  minutes to complete.
                </Typography>
                <Stack spacing={3} sx={{ mt: 1 }}>
                  <DatePickerField
                    name={FieldNames.from}
                    label="Start date"
                    variant="outlined"
                    size="small"
                    disableFuture={true}
                  />
                  <DatePickerField
                    name={FieldNames.to}
                    label="End date"
                    variant="outlined"
                    size="small"
                    disableFuture={true}
                  />
                </Stack>
                {createError && (
                  <Box sx={{ mt: 2 }}>
                    <ApiErrorMessage apiError={createError} />
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <LoadingButton variant="contained" isLoading={isCreating} color="primary">
                  Generate Report
                </LoadingButton>
                <Button onClick={handleCloseDialog} disabled={isCreating}>
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          </Formik>
        </Dialog>
      )}
    </PageLayout>
  );
};

export { FileTypesByClientReportsPage };
